import React, { useMemo } from 'react';
import { Group, MultiSelect, NumberInput, Select, TagsInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { ConfigFormValues, getQuestionType, operatorOptions } from './alertConfigurationConstants.ts';
import { IJournalCategory } from '../../../../global/entities/Journal/Journal.ts';
import { QUESTION_TYPE_IDS } from '../../../PatientDashboard/PatientJournal/QuestionTypes.ts';
import TrendFields from './TrendFields.tsx';

interface Props {
    form: UseFormReturnType<ConfigFormValues>;
    questionOptions: { group: string; items: { value: string; label: string }[] }[];
    categories: IJournalCategory[] | undefined;
    isEn: boolean;
    evaluationMode: string;
}

const NUMERIC_TYPES = [
    QUESTION_TYPE_IDS.YES_NO_WITH_SCALE,
    QUESTION_TYPE_IDS.YES_NO_WITH_COUNT,
    QUESTION_TYPE_IDS.SCALE,
    QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE,
];

const SELECT_TYPES = [
    QUESTION_TYPE_IDS.YES_NO,
    QUESTION_TYPE_IDS.MULTI_SELECT,
    QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT,
];

const JournalConditionFields: React.FC<Props> = ({ form, questionOptions, categories, isEn, evaluationMode }) => {
    const { t } = useTranslation();

    const selectedQuestion = useMemo(
        () => getQuestionType(form.values.conditionQuestionId, categories),
        [form.values.conditionQuestionId, categories],
    );

    const isNumericQuestion = selectedQuestion != null && NUMERIC_TYPES.includes(selectedQuestion.questionTypeID);
    const isSelectQuestion = selectedQuestion != null && SELECT_TYPES.includes(selectedQuestion.questionTypeID);
    const isTextQuestion = selectedQuestion != null && selectedQuestion.questionTypeID === QUESTION_TYPE_IDS.FREE_TEXT;

    const triggerValueOptions = useMemo(() => {
        if (!selectedQuestion) return [];
        if (selectedQuestion.questionTypeID === QUESTION_TYPE_IDS.YES_NO) {
            return [
                { value: 'Yes', label: isEn ? 'Yes' : 'Oui' },
                { value: 'No', label: isEn ? 'No' : 'Non' },
            ];
        }
        if (!selectedQuestion.optionsJson) return [];
        try {
            const parsed = JSON.parse(selectedQuestion.optionsJson);
            if (selectedQuestion.questionTypeID === QUESTION_TYPE_IDS.MULTI_SELECT) {
                return (parsed.options || []).map((o: { valueEn: string; valueFr: string }) => ({
                    value: o.valueEn,
                    label: isEn ? o.valueEn : o.valueFr,
                }));
            }
            if (selectedQuestion.questionTypeID === QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT) {
                const primaryEn = parsed.primaryOptionsEn || [];
                const secondaryEn = parsed.secondaryOptionsEn || [];
                const primaryFr = parsed.primaryOptionsFr || [];
                const secondaryFr = parsed.secondaryOptionsFr || [];
                const allEn = [...primaryEn, ...secondaryEn];
                const allFr = [...primaryFr, ...secondaryFr];
                return allEn.map((en: string, i: number) => ({
                    value: en,
                    label: isEn ? en : (allFr[i] || en),
                }));
            }
            return [];
        } catch {
            return [];
        }
    }, [selectedQuestion, isEn]);

    return (
        <>
            <Select
                label={t('alertRule.form.question')}
                placeholder={t('alertRule.form.questionPlaceholder')}
                data={questionOptions}
                searchable
                {...form.getInputProps('conditionQuestionId')}
            />
            {isNumericQuestion && evaluationMode === 'trend' && (
                <TrendFields form={form} showCategory={false} />
            )}
            {isNumericQuestion && evaluationMode !== 'trend' && (
                <Group grow align="flex-start">
                    <Select
                        label={t('alertRule.form.operator')}
                        data={operatorOptions}
                        {...form.getInputProps('conditionOperator')}
                    />
                    <NumberInput
                        label={t('alertRule.form.value')}
                        placeholder="0"
                        decimalScale={2}
                        {...form.getInputProps('conditionThreshold')}
                    />
                </Group>
            )}
            {isSelectQuestion && (
                <MultiSelect
                    label={t('alertRule.form.triggerValues')}
                    placeholder={t('alertRule.form.triggerValuesPlaceholder')}
                    data={triggerValueOptions}
                    searchable
                    {...form.getInputProps('conditionTriggerValues')}
                />
            )}
            {isTextQuestion && (
                <TagsInput
                    label={t('alertRule.form.keywords')}
                    placeholder={t('alertRule.form.keywordsPlaceholder')}
                    {...form.getInputProps('conditionKeywords')}
                />
            )}
        </>
    );
};

export default JournalConditionFields;
